import type { SupabaseClient } from '@supabase/supabase-js'
import { useCallback } from 'react'

import { useAuth } from '@/hooks/useAuth'
import { useSupabaseClient } from '@/hooks/useSupabaseClient'
import { postHogTrackingService } from '@/services/PostHogTrackingService'

export enum PostHogEventName {
  UserFirstProductCreated = 'user_first_product_created',
  UserSignupCompleted = 'user_signup_completed',
  UserFirstRoadmapItemCreated = 'user_first_roadmap_item_created',
  UserFirstExperimentCreated = 'user_first_experiment_created',
  UserFirstInsightCreated = 'user_first_insight_created',
  UserFirstPromptSubmitted = 'user_first_prompt_submitted',
  UpgradeToPremiumClicked = 'upgrade_to_premium_clicked',
  UserFirstLoginSubmitted = 'user_first_login_completed',
  UserFirstLearningModuleGenerated = 'user_first_learning_module_generated',
  UserFirstToolUsed = 'user_first_tool_used',
  ShortcutClicked = 'shortcut_clicked',
  FeedbackModalOpened = 'feedback_modal_opened',
  FeedbackSubmitted = 'feedback_submitted',
  FeedbackModalDismissed = 'feedback_modal_dismissed',
  DraftWithStellaClicked = 'draft_with_stella_clicked',
  StellaBubbleClicked = 'stella_bubble_clicked',
}

const ONE_TIME_EVENTS = new Set<string>([
  PostHogEventName.UserFirstProductCreated,
  PostHogEventName.UserSignupCompleted,
  PostHogEventName.UserFirstRoadmapItemCreated,
  PostHogEventName.UserFirstExperimentCreated,
  PostHogEventName.UserFirstInsightCreated,
  PostHogEventName.UserFirstPromptSubmitted,
  PostHogEventName.UserFirstLoginSubmitted,
  PostHogEventName.UserFirstLearningModuleGenerated,
  PostHogEventName.UserFirstToolUsed,
])

const POSTHOG_EVENTS_TABLE = 'posthog_events'

const trackOneTimeEvent = async (
  supabase: SupabaseClient,
  userId: string,
  eventName: string,
  properties?: Record<string, unknown>
): Promise<void> => {
  try {
    if (!postHogTrackingService.willCapture()) {
      return
    }

    const { data: existingEvent, error: selectError } = await supabase
      .from(POSTHOG_EVENTS_TABLE)
      .select('id')
      .eq('user_id', userId)
      .eq('event_name', eventName)
      .maybeSingle()

    if (!selectError && existingEvent) {
      return
    }

    if (selectError) {
      console.error(
        `Error checking PostHog event ${eventName} in database:`,
        selectError
      )
    }

    postHogTrackingService.trackEvent(eventName, properties)

    const { error: upsertError } = await supabase
      .from(POSTHOG_EVENTS_TABLE)
      .upsert(
        {
          user_id: userId,
          event_name: eventName,
        },
        {
          onConflict: 'user_id,event_name',
        }
      )

    if (upsertError) {
      console.error(
        `Error recording PostHog event ${eventName} in database:`,
        upsertError
      )
    }
  } catch (error) {
    console.error(`Error tracking PostHog event ${eventName}:`, error)
  }
}

export const usePostHogTracking = () => {
  const { user, isLoaded } = useAuth()
  const supabase = useSupabaseClient()

  const trackEvent = useCallback(
    async (
      eventName: PostHogEventName,
      properties?: Record<string, unknown>,
      forceLoaded: boolean = false
    ): Promise<void> => {
      if (!forceLoaded && (!isLoaded || !user)) {
        return
      }

      const resolvedId: string = user?.id ?? (properties?.user_id as string)
      const userId = forceLoaded ? resolvedId : user?.id

      const isOneTimeEvent = ONE_TIME_EVENTS.has(eventName)

      if (!isOneTimeEvent) {
        postHogTrackingService.trackEvent(eventName, properties)
        return
      }

      if (userId) {
        await trackOneTimeEvent(supabase, userId, eventName, properties)
      }
    },
    [isLoaded, user, supabase]
  )

  return {
    trackEvent,
  }
}
